import { __awaiter, __generator } from "tslib";
import { VoiceCommandResultV2, WordVoiceCommandIds, } from "@augloop-types/voice";
import * as AugLoopUtils from "./utils/AugLoopUtils";
import { sendSpeechToTextFinalResultToSpeechClientObserver, SpeechToTextClient, } from "./SpeechToTextClient";
import { getValidCommandSet, hasValidCommandValues, alterCommandValue, } from "./utils/validCommandUtil";
import { ULS, TraceLevel, LogCategory } from "@1js/office-online-telemetry";
import { sendTelemetryEvent } from "@1js/office-online-otel";
import { getBooleanFeatureGate } from "@1js/office-online-app-platform";
var releaseStaleVoiceAnnotations = function () {
    return getBooleanFeatureGate("Microsoft.Office.WordOnline.ReleaseStaleVoiceAnnotations", 
    /*defaultValue*/ false);
};
// Represents a will receive voice commands from AL.
var VoiceCommandingClient = /** @class */ (function () {
    function VoiceCommandingClient(alRuntimeManager, fdVoiceCommandClientObserver, fdSpeechClientObserver, enabledExperimentalCommandSetNames, enableSetAnnotationsFromALVoiceClient) {
        var _this = this;
        this.initialized = false;
        this.voiceCommandRequestToken = null;
        this.alRuntimeManager = alRuntimeManager;
        this.fdVoiceCommandClientObserver = fdVoiceCommandClientObserver;
        this.fdSpeechClientObserver = fdSpeechClientObserver;
        this.enableSetAnnotationsFromALVoiceClient =
            enableSetAnnotationsFromALVoiceClient;
        this.validVoiceCommands = getValidCommandSet(enabledExperimentalCommandSetNames);
        this.annotationObserver = {
            onAnnotationResult: function (op) {
                _this.onAnnotationResult(op);
            },
        };
    }
    // Initialize voice commanding client.
    VoiceCommandingClient.prototype.initializeAsync = function (sessionTelemetry) {
        return __awaiter(this, void 0, void 0, function () {
            var annotationsActive, _a, err_1, msg;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        annotationsActive = this.voiceCommandRequestToken !== null;
                        ULS.sendTraceTag(0x200c331e /* tag_6ddm4 */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Info, "VoiceCommandingClient.initializeAsync - initialized: ".concat(this.initialized, ", annotationsActive: ").concat(annotationsActive));
                        if (!annotationsActive) return [3 /*break*/, 3];
                        if (!releaseStaleVoiceAnnotations()) return [3 /*break*/, 2];
                        // release stale annotations before trying to activate again
                        ULS.sendTraceTag(0x2011d391 /* tag_6e3or */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Warning, "Releasing old annotations");
                        return [4 /*yield*/, this.uninitializeAsync()];
                    case 1:
                        _b.sent();
                        return [3 /*break*/, 3];
                    case 2: throw new Error("VoiceCommandingClient was already initialized.");
                    case 3:
                        if (sessionTelemetry)
                            this.sessionTelemetry = sessionTelemetry;
                        _b.label = 4;
                    case 4:
                        _b.trys.push([4, 6, , 7]);
                        _a = this;
                        return [4 /*yield*/, AugLoopUtils.activateAnnotationAsync(this.alRuntimeManager, VoiceCommandResultV2.getTypeName(), this.annotationObserver, this.enableSetAnnotationsFromALVoiceClient)];
                    case 5:
                        _a.voiceCommandRequestToken =
                            _b.sent();
                        this.initialized = true;
                        return [3 /*break*/, 7];
                    case 6:
                        err_1 = _b.sent();
                        msg = "VoiceCommandingClient.initializeAsync - Activate Annotations - " +
                            err_1.message;
                        /**
                         * As long as AL Websocket timeout, AL will point client to a new Websocket. However, AL sub/pub keeps sending.
                         * the client "Timeout waiting for response" error message even client is dictation, which doesn't impact the dictation working.
                         * The issue needs to be fixed from infrastructural side,
                         * AL has an User Story to fix the error: https://office.visualstudio.com/DefaultCollection/GSX/_workitems/edit/4779001
                         */
                        if (msg.includes("Timeout waiting for response")) {
                            this.fdSpeechClientObserver.onWarning(err_1);
                        }
                        else {
                            this.fdSpeechClientObserver.onError(err_1);
                        }
                        return [3 /*break*/, 7];
                    case 7: return [2 /*return*/];
                }
            });
        });
    };
    // Uninitialize voice commanding client.
    VoiceCommandingClient.prototype.uninitializeAsync = function () {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            var annotationsActive, err_2, msg;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        annotationsActive = this.voiceCommandRequestToken !== null;
                        ULS.sendTraceTag(0x200c331f /* tag_6ddm5 */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Info, "VoiceCommandingClient.uninitializeAsync - initialized: ".concat(this.initialized, ", annotationsActive: ").concat(annotationsActive));
                        this.initialized = false;
                        if (!annotationsActive) return [3 /*break*/, 5];
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, AugLoopUtils.releaseAnnotations(this.alRuntimeManager, [
                                this.voiceCommandRequestToken,
                            ])];
                    case 2:
                        _b.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        err_2 = _b.sent();
                        msg = "VoiceCommandingClient.uninitializeAsync - Release Annotations - " +
                            err_2.message;
                        (_a = this.sessionTelemetry) === null || _a === void 0 ? void 0 : _a.onAugloopError(msg);
                        ULS.sendTraceTag(0x223096d2 /* tag_8mj1s */, LogCategory.msoulscat_Wac_Dictation, TraceLevel.Error, "VoiceCommandingClient.uninitializeAsync - Release Annotations - Error message: " +
                            msg +
                            " - Error stack: " +
                            err_2.stack);
                        return [3 /*break*/, 4];
                    case 4:
                        this.voiceCommandRequestToken = null;
                        _b.label = 5;
                    case 5: return [2 /*return*/];
                }
            });
        });
    };
    // Return an annotation observer implemented by us that app will use to forward annotations to us.
    VoiceCommandingClient.prototype.getAnnotationObserver = function () {
        return this.annotationObserver;
    };
    // Handle an annotation result.
    VoiceCommandingClient.prototype.onAnnotationResult = function (op) {
        var _this = this;
        var voiceCommandItems = AugLoopUtils.filterOperationByAnnotationTypeName(op, VoiceCommandResultV2.getTypeName());
        voiceCommandItems.forEach(function (item) {
            _this.handleCommandResult(item.body);
        });
    };
    /** Handler for voice command V2 format */
    VoiceCommandingClient.prototype.handleCommandResult = function (commandResult) {
        var _a, _b, _c, _d, _e, _f;
        if (commandResult.error && this.sessionTelemetry) {
            this.sessionTelemetry.onCommandError("handleCommandResult:".concat(commandResult.error));
        }
        var phraseId = (_b = (_a = commandResult.textAnnotation.properties) === null || _a === void 0 ? void 0 : _a.JsonResult) === null || _b === void 0 ? void 0 : _b.Id;
        for (var _i = 0, _g = commandResult.commandList; _i < _g.length; _i++) {
            var command = _g[_i];
            var isCommandSupported = this.validVoiceCommands.has(command.commandId) &&
                hasValidCommandValues(command);
            if (!isCommandSupported) {
                sendTelemetryEvent({
                    name: "Office.Voice.Online.UnsupportedCommandReceived",
                    dataFields: [
                        { name: "PhraseID", string: phraseId },
                        {
                            name: "ConnectionID",
                            string: (_c = this.sessionTelemetry) === null || _c === void 0 ? void 0 : _c.connectionID,
                        },
                        { name: "SessionID", string: (_d = this.sessionTelemetry) === null || _d === void 0 ? void 0 : _d.sessionID },
                        { name: "CommandID", string: command.commandId },
                        { name: "CommandVersion", int64: command.ver },
                    ],
                });
            }
            // If type is InsertText or if this command is not supported, show it as a text
            if (command.commandId === WordVoiceCommandIds.InsertText ||
                !isCommandSupported) {
                sendSpeechToTextFinalResultToSpeechClientObserver(this.fdSpeechClientObserver, commandResult.textAnnotation, command.sourceText, // FinalText
                this.sessionTelemetry);
            }
            else {
                // alter command if necessary
                var alteredCommand = alterCommandValue(command);
                this.fdVoiceCommandClientObserver.onVoiceCommand(alteredCommand, phraseId);
                // and log the phraseId
                SpeechToTextClient.logPhraseId(phraseId, (_e = this.sessionTelemetry) === null || _e === void 0 ? void 0 : _e.connectionID, (_f = this.sessionTelemetry) === null || _f === void 0 ? void 0 : _f.sessionID, "final");
            }
        }
    };
    return VoiceCommandingClient;
}());
export { VoiceCommandingClient };
//# sourceMappingURL=VoiceCommandingClient.js.map